import type React from "react";
import { useState } from "react";
import { connect } from "react-redux";

import { updateSegmentData } from "app/actions/conversionModuleActions";
import type { ICountryObject } from "app/services/CountryService";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { i18nFilter } from "app/filters/i18nFilter";
import ConversionApiService from "../../../services/conversion/conversionApiService";
import type { ISegmentsData } from "app/services/conversion/ConversionSegmentsService";
import { allTrackers } from "app/services/track/track";
import { ConversionSegmentsUtils } from "../ConversionSegmentsUtils";
import type { IWizardCountries, IWizardCountry, IWizardMarkets } from "./CustomGroupWizard";
import { CustomGroupWizard } from "./CustomGroupWizard";
import { useCurrentStateAndParams } from "@uirouter/react";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { resolveRouterTrackingId } from "single-spa/react-app/router/resolveRouterTrackingId";

const DEFAULT_DURATION = "6m";
const DEFAULT_COUNTRY = "840";

declare const window;

interface IStartupSegments {
    [id: string]: IStartupSegment;
}

export interface IStartupSegment {
    countries: number[];
    creationType: string;
    domain: string;
    id: string;
    isSingleLob: boolean;
    segmentName: string;
}

interface ICountryHashData {
    [id: number]: ICountryObject;
}

interface IStartupSegmentGroups {
    [id: string]: {
        creationType: string;
        id: string;
        name: string;
        segments: string[];
    };
}

interface ISegmentData {
    creationType?: string;
    id: string;
    name?: string;
    segments: string[];
}

interface ICustomGroupWizardContainerProps {
    segments: ISegmentsData;
    updateSegments: (isNew: boolean, segment: ISegmentData) => void;
    showErrorToast: (text?: string) => void;
    showSuccessToast: (text?: string) => void;
}

/**
 * Collect available countries based on countries data and available under segments
 *
 * @param {IStartupSegments} segments
 * @param {ICountryHashData} countries
 *
 * @returns {ICountryData}
 */
function gatherAvailableCountries(segments: IStartupSegments, countries: ICountryHashData): IWizardCountries {
    return Object.keys(segments).reduce((result, segmentId: string) => {
        const segment = segments[segmentId];

        segment.countries.forEach((countryId) => {
            if (!result[countryId]) {
                result[countryId] = {
                    ...countries[countryId],
                    markets: [],
                };
            }
        });

        return result;
    }, {});
}

function gatherAvailableMarkets(segmentsData: ISegmentsData, allAvailableCountries: IWizardCountries) {
    // const industries: IStartupSegmentIndustry[] = segmentsData.segmentIndustries || [];
    const groups: IStartupSegmentGroups = segmentsData.segmentGroups || {};
    const toUppercaseFirstLetters = (str: string) => {
        return str
            .trim()
            .split(" ")
            .reduce((result, word) => {
                result += `${word.charAt(0).toUpperCase()}${word.slice(1)} `;

                return result;
            }, "")
            .trim();
    };
    const buildCountryData = (countries: IWizardCountry[]): IWizardCountries => {
        return countries.reduce((countriesAcc, { id, text }) => {
            countriesAcc = {
                ...countriesAcc,
                [id]: {
                    id,
                    text,
                },
            };

            return countriesAcc;
        }, {});
    };

    const result = {
        all: {
            text: i18nFilter()("conversion.wizard.markets.allMarketsLabel"),
            countries: allAvailableCountries,
            id: "all",
        },
    };

    Object.keys(groups).forEach((groupId) => {
        const name = groups[groupId].name || "";
        result[groupId] = {
            id: groupId,
            gid: groupId,
            creationType: groups[groupId].creationType,
            text: toUppercaseFirstLetters(name),
            countries: buildCountryData(ConversionSegmentsUtils.getSegmentGroupCountries(segmentsData, groupId)),
        };
    });

    return result;
}

const onGroupConfirmCbCreator =
    (api, isEdit, updateSegments, swNavigator, showErrorToast, showSuccessToast, state, setIsFetching, duration) =>
    async ({ name, segmentIds, groupId }) => {
        let response;
        setIsFetching(true);
        try {
            response = isEdit
                ? await api.updateSegmentGroup(groupId, name, segmentIds)
                : await api.createSegmentGroup(name, segmentIds);

            showSuccessToast(i18nFilter()(`conversion.wizard.toast.success.${isEdit ? "edit" : "create"}`));
            updateSegments(!isEdit, response);
            allTrackers.trackEvent(
                "Button",
                "Click",
                `${isEdit ? "Confirm edit" : "Confirm create"}/${response.segments.length}/${response.id}`,
            );
            window.setTimeout(() => {
                setIsFetching(false);
                swNavigator.go(
                    "conversion-customgroup",
                    {
                        gid: response.id,
                        country: DEFAULT_COUNTRY,
                        duration: duration || DEFAULT_DURATION,
                    },
                    { reload: true },
                );
            }, 1500);
        } catch (e) {
            showErrorToast(i18nFilter()("conversion.wizard.toast.fail"));
            setIsFetching(false);
        }
    };

// to add markets to countries
function enrichCountriesWithMarkets(countries: IWizardCountries, markets: IWizardMarkets): IWizardCountries {
    const result = { ...countries };

    Object.keys(markets).forEach((marketId) => {
        const marketData = markets[marketId];

        Object.keys(marketData.countries).forEach((countryId) => {
            result[countryId].markets.push(marketData.id);
        });
    });

    return result;
}

const CustomGroupWizardContainer: React.FunctionComponent<ICustomGroupWizardContainerProps> = ({
    showErrorToast,
    showSuccessToast,
    updateSegments,
    segments: segmentsData,
}) => {
    const { state, params } = useCurrentStateAndParams();
    const { gid, country, duration } = params;
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const segments = segmentsData.segments || {};
    let countries = gatherAvailableCountries(segments, CountryService.countriesById);
    const markets = gatherAvailableMarkets(segmentsData, countries);
    countries = enrichCountriesWithMarkets(countries, markets);
    const { from, to } = DurationService.getDurationData("1m").forAPI;
    const pageFilters: any = {
        gid,
        country,
        from,
        to,
    };
    const api = new ConversionApiService();
    const isEdit = !!pageFilters.gid;
    const selectedGroup = isEdit
        ? ConversionSegmentsUtils.getSelectedGroupEnrichedWithSegmentsData(segmentsData, pageFilters.gid)
        : undefined;
    const title = isEdit
        ? i18nFilter()("conversion.wizard.pageTitle.edit", {
              groupName: selectedGroup?.name,
          })
        : i18nFilter()("conversion.wizard.pageTitle.create");
    const subtitle = i18nFilter()("conversion.wizard.pageSubtitle");
    const onGroupConfirm = onGroupConfirmCbCreator(
        api,
        isEdit,
        updateSegments,
        proNavigator,
        showErrorToast,
        showSuccessToast,
        state,
        setIsFetching,
        duration,
    );

    return (
        <CustomGroupWizard
            title={title}
            subtitle={subtitle}
            pageFilters={pageFilters}
            translate={i18nFilter()}
            selectedGroup={selectedGroup}
            markets={markets}
            countries={countries}
            segments={segments}
            segmentsData={segmentsData}
            trackingKey={resolveRouterTrackingId(state, params)?.subSubSection}
            onConfirm={onGroupConfirm}
            isFetching={isFetching}
            track={allTrackers.trackEvent.bind(allTrackers)}
        />
    );
};

function mapStateToProps({ conversionModule: { segments } }) {
    return {
        segments,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        updateSegments: (isNew: boolean, segment: ISegmentData) => dispatch(updateSegmentData(isNew, segment)),
        showErrorToast: (text?: string) => dispatch(showErrorToast(text)),
        showSuccessToast: (text?: string) => dispatch(showSuccessToast(text)),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(CustomGroupWizardContainer);
